import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

import { FieldMessages } from './FieldMessages';
import { ICONS } from './icons';
import { useFieldLock } from './row-viewer';

/**
 * The wiring `Field` hands to the control. Spread it, unchanged, on the input/select.
 *
 * `disabled` and `readOnly` are part of it because the FRAME has to know them too — the outline
 * and the label are drawn by `Field`, not by the control — and a form should declare a state
 * once rather than in two places that can disagree.
 */
export interface FieldControlProps {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': boolean | undefined;
  disabled: boolean | undefined;
  readOnly: boolean | undefined;
}

export type FieldState = 'default' | 'invalid' | 'readonly' | 'disabled';

export interface FieldProps {
  label: string;
  description?: string;
  error?: string;
  /**
   * STJERNEN PÅ ET PÅKREVD FELT (QA13 runde 3, blokk 3 — rest 31a).
   *
   * Atle, runde 2: veiviseren nektet uten å si hva som manglet. Halvparten ble rettet da —
   * knappen sier nå «Mangler navn (steg 1 av 5)» framfor å bare være grå. Denne er den andre
   * halvparten: å se HVILKE felt som må fylles ut FØR man trykker.
   *
   * `optionalLabel` er den motsatte konvensjonen og de skal ikke brukes samtidig: et skjema
   * merker enten det som MÅ fylles ut eller det som KAN, aldri begge, ellers må leseren telle to
   * sett merker for å finne ut hva som gjenstår.
   *
   * Stjernen er `aria-hidden` og ordet «påkrevd» står ved siden av i skjult tekst: en `*` lest
   * opp som «stjerne» er ikke en beskjed, og den hører hjemme i det tilgjengelige navnet som ord.
   */
  required?: boolean;
  optionalLabel?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /**
   * FELTNAVNET SLIK API-ET KJENNER DET (`displayName`, `phone`) — D1, lesemodus.
   *
   * Med det spør feltet `viewer.lockedFields` fra nærmeste `RowViewerProvider` og blir
   * skrivebeskyttet med grunnen under seg når API-et har låst akkurat det. Uten `canEdit` er
   * feltet skrivebeskyttet uansett navn. Se `row-viewer.tsx`.
   */
  name?: string;
  className?: string;
  /** Receives the wiring: id, aria-describedby, aria-invalid, disabled, readOnly. */
  children: (props: FieldControlProps) => ReactElement;
}

/** The keyframes `globals.css` starts on an autofilled control. Nothing else uses the name. */
const AUTOFILL_ANIMATION = 'tvitvi-autofill';

/**
 * The text-entry control inside the frame, if there is one.
 *
 * `[type="hidden"]` is excluded because a form-associated control that nobody can see has no
 * bearing on whether the field looks empty.
 */
function textControlIn(frame: HTMLElement | null): HTMLInputElement | HTMLTextAreaElement | null {
  const node = frame?.querySelector('input:not([type="hidden"]), textarea') ?? null;
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement ? node : null;
}

/**
 * Is this node inside a panel that belongs to the field with this id?
 *
 * A control that opens a surface — `TimeField`'s panel — portals it to `document.body`, so to
 * the DOM it is a different part of the page entirely. It is not. The control stamps it with
 * `data-field-surface={id}` and focus inside it counts as focus in the field, which is what
 * stops the label from falling back into a field somebody is in the middle of filling in.
 */
function inSurfaceOf(node: EventTarget | null, id: string): boolean {
  let element = node instanceof Element ? node : null;
  for (; element !== null; element = element.parentElement) {
    if (element.getAttribute('data-field-surface') === id) return true;
  }
  return false;
}

/**
 * A text-entry field: a tonal fill with its name inside it — and floating.
 *
 * GLASS 2 (tolkning 1). The field is FILLED and has no edge at rest; the edge only appears for
 * focus (the focus rule, §9) and for an error. The name that used to sit in a notch in the outline
 * now sits in the same place inside the fill, at the top, so a form keeps «the label in the frame»
 * without a frame line. The markup below did not change — the fieldset still paints the edge, the
 * legend still holds the real label — only `globals.css` did.
 *
 * WHERE THE LABEL IS (DESIGN.md §13). Empty and unfocused, it sits inside the field on the line
 * the value will land on, at the value's size: it reads as a placeholder and is a real
 * `<label>`, so it survives the first keystroke, which a `placeholder` does not (§9). Focused,
 * or holding a value, it slides up into the notch and the border opens to receive it. The
 * drawing of both states, and the timing that keeps the notch in step with the label, is the
 * block above `.field-frame` in `globals.css`.
 *
 * HOW THIS COMPONENT KNOWS. `data-field-float` on the frame is the whole switch, and it is
 * computed from three things, in this order of reliability:
 *
 *   1. THE CONTROL'S OWN VALUE, re-read after every render. That covers a field rendered with a
 *      value, a value changed by the form library rather than by typing, and a value cleared by
 *      a reset — none of which are events this component could listen for.
 *   2. FOCUS, tracked on the document — which includes focus inside a panel the control opened
 *      and portalled out of the frame, because that is still focus in the field.
 *   3. THE AUTOFILL ANIMATION. A browser filling a field on load does not reliably fire an
 *      input event; it does start the keyframes `globals.css` attaches to `:autofill`. Without
 *      this the label would sit on top of the autofilled text.
 *
 * A control with no text entry in it at all — a `Select` trigger, anything that always shows
 * something — is permanently floated: it has no empty state for the label to stand in.
 *
 * WHAT THE FIRST PAINT DOES. Nothing moves. `data-field-motion` is what switches the transitions
 * on and it is set a frame after mount, so a pre-filled field is drawn floated and stays there.
 * Only a real state change animates.
 *
 * WHAT DID NOT CHANGE. `htmlFor`/`id`, `aria-describedby` and `aria-invalid` are wired exactly
 * as they were before the label moved, and every field state is the state it was. This is a
 * change of where the label sits over time, and nothing else.
 *
 * WHAT THE MARKUP IS DOING. The `<fieldset class="field-outline">` is not a grouping construct
 * here — `role="none"` takes its `group` semantics away — it is a border painter. Its `<legend>`
 * is what makes the browser interrupt that border, and the real `<label htmlFor>` lives inside
 * the legend, so the notch and the accessible name are the same element. There is no second,
 * hidden copy of the label to keep in sync, and nothing anywhere paints a background to fake
 * the gap; see the block comment above `.field-frame` in `globals.css` for why that matters.
 *
 * WHAT IT COSTS. 46px for a labelled field, exactly as before Glass 2 — all of it fill now. The
 * label-above-input version was ~56px.
 */
export function Field({
  label,
  description: descriptionProp,
  error,
  required = false,
  optionalLabel,
  disabled = false,
  readOnly: readOnlyProp = false,
  name,
  className,
  children,
}: FieldProps): ReactNode {
  const { t } = useTranslation();
  const id = useId();
  const lock = useFieldLock(name);
  const readOnly = readOnlyProp || lock.readOnly;
  /* Grunnen står der hjelpeteksten står — den ER hjelpeteksten for et låst felt. */
  const description =
    lock.reason === null
      ? descriptionProp
      : descriptionProp === undefined
        ? lock.reason
        : `${lock.reason} ${descriptionProp}`;
  const descriptionId = `${id}-description`;
  const errorId = `${id}-error`;
  const describedBy = [description ? descriptionId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  const frameRef = useRef<HTMLDivElement>(null);
  const [filled, setFilled] = useState(false);
  const [focused, setFocused] = useState(false);
  const [motionReady, setMotionReady] = useState(false);

  const syncFilled = useCallback(() => {
    const control = textControlIn(frameRef.current);
    // No text entry in the frame: a Select trigger, or anything else that always shows
    // something. There is no empty state to reveal, so the label stays in the notch.
    setFilled(control === null || control.value !== '');
  }, []);

  // Deliberately on EVERY render and deliberately a layout effect: the value may have been
  // changed by a parent, and the answer has to be in the DOM before the browser paints, or a
  // pre-filled field would paint once with its label across the value.
  useLayoutEffect(syncFilled);

  /*
   * Focus is tracked on the DOCUMENT, not with onFocus/onBlur on the frame, because "the field
   * has focus" is not the same question as "the frame contains the active element". A control
   * that opens a panel moves focus into something portalled outside the frame, and a blur
   * handler on the frame would call that "focus left the field" and drop the label back into a
   * field the user is in the middle of using. `focusin` tells us where focus went, wherever it
   * went; `focusout` with no relatedTarget is the one case no `focusin` follows.
   */
  useEffect(() => {
    const owns = (node: EventTarget | null): boolean =>
      (node instanceof Node && frameRef.current?.contains(node) === true) || inSurfaceOf(node, id);

    const onFocusIn = (event: FocusEvent): void => setFocused(owns(event.target));
    const onFocusOut = (event: FocusEvent): void => {
      if (event.relatedTarget === null && owns(event.target)) setFocused(false);
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [id]);

  useEffect(() => {
    const frame = frameRef.current;
    if (frame === null) return undefined;

    const onAnimationStart = (event: AnimationEvent): void => {
      if (event.animationName === AUTOFILL_ANIMATION) syncFilled();
    };
    frame.addEventListener('animationstart', onAnimationStart);

    // Two frames, not one: a callback scheduled during the commit still runs before the paint
    // of that same frame, and switching the transitions on before the first paint is exactly
    // what would let a pre-filled field animate its label in from the middle of the field.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setMotionReady(true));
    });

    return () => {
      frame.removeEventListener('animationstart', onAnimationStart);
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [syncFilled]);

  // Disabled outranks the rest: a control you cannot reach cannot be corrected, so telling
  // someone it is also invalid is noise. Then the error, then read-only.
  const state: FieldState = disabled
    ? 'disabled'
    : error
      ? 'invalid'
      : readOnly
        ? 'readonly'
        : 'default';

  const Lock = ICONS.lock;

  return (
    /*
     * ANKERET Å RULLE TIL (blokk 3, rest 31a). `data-field-invalid` staar paa den ytre boksen og
     * ikke paa kontrollen, fordi det som skal i syne er feltet MED etiketten og feilmeldingen —
     * ruller man til selve input-en, havner beskjeden under skjermkanten.
     */
    <div
      className={cn('flex min-w-0 flex-col', className)}
      data-field-invalid={error ? 'true' : undefined}
    >
      <div
        ref={frameRef}
        className="field-frame"
        data-field-state={state}
        data-field-float={focused || filled ? 'true' : 'false'}
        data-field-motion={motionReady ? 'ready' : undefined}
        // change bubbles, so the frame hears it from whichever control is inside it without
        // every control having to report itself. Focus is handled above, on the document.
        onChange={syncFilled}
      >
        {children({
          id,
          'aria-describedby': describedBy.length > 0 ? describedBy : undefined,
          'aria-invalid': error ? true : undefined,
          disabled: disabled || undefined,
          readOnly: readOnly || undefined,
        })}
        {readOnly && !disabled ? (
          <Lock aria-hidden data-slot="field-lock" className="field-lock size-3.5" />
        ) : null}
        {/* role="none" so an outline drawn with a fieldset does not announce itself as a group
            around a single input. The label inside stays a real label and is NOT hidden from
            assistive technology — hiding it would take the control's accessible name with it. */}
        <fieldset role="none" className="field-outline">
          <legend className="field-notch">
            <label htmlFor={id} className="field-label">
              {label}
              {required ? (
                <>
                  {/* Ordet først, som tekst, så det tilgjengelige navnet sier «påkrevd» og ikke
                      «stjerne». Stjernen er bildet av det samme for den som ser.

                      `{' '}` og ikke et mellomrom i kildeteksten: JSX spiser mellomrom ved et
                      linjeskift, og «E-postpåkrevd» er hva en skjermleser da får. Samme grep som
                      `optionalLabel` under, og av nøyaktig samme grunn. */}{' '}
                  <span className="sr-only">{t('common.field.required')}</span>
                  <span aria-hidden className="field-required">
                    *
                  </span>
                </>
              ) : null}
              {optionalLabel ? (
                <>
                  {/* A real space, not a CSS margin: the accessible name is built from TEXT, and
                      "E-postvalgfritt" is what a margin gives a screen reader. */}{' '}
                  <span className="field-optional">{optionalLabel}</span>
                </>
              ) : null}
            </label>
          </legend>
        </fieldset>
      </div>
      <FieldMessages
        descriptionId={descriptionId}
        description={description}
        errorId={errorId}
        error={error}
      />
    </div>
  );
}
